"use client";

import Image from "next/image";
import { AnimateIn } from "@/components/ui/AnimateIn";
import { useLang } from "@/context/LanguageContext";

export function Work() {
  const { t } = useLang();
  const w = t.work;

  return (
    <section
      id="work"
      aria-labelledby="work-heading"
      className="relative overflow-hidden border-t border-[var(--card-border)] bg-[var(--background-2)] px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28"
    >
      <div
        className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-[var(--accent-teal)]/[0.04] blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-16 bottom-1/4 h-64 w-64 rounded-full bg-[var(--accent)]/[0.05] blur-3xl"
        aria-hidden
      />

      <div className="relative mx-auto max-w-6xl">
        <AnimateIn animation="slide-right" className="flex items-end justify-between gap-6">
          <div>
            <p className="section-label">
              <span aria-hidden>◆</span> {w.label}
            </p>
            <h2
              id="work-heading"
              className="font-display mt-4 text-3xl font-bold tracking-tight text-[var(--foreground)] sm:text-4xl"
            >
              {w.heading}
            </h2>
            <div className="title-underline" />
          </div>
          <a
            href="#contact"
            className="mb-1 hidden items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--muted-foreground)] transition-colors hover:text-[var(--accent-teal)] sm:inline-flex"
          >
            {w.allProjects}
          </a>
        </AnimateIn>

        <ul className="work-grid mt-10 sm:mt-16">
          {w.projects.map((project, i) => {
            const index = String(i + 1).padStart(2, "0");
            const featured = i === 0;

            return (
              <AnimateIn
                key={project.title}
                as="li"
                animation="fade-up"
                delay={i * 130}
                className={featured ? "work-card work-card-featured group" : "work-card group"}
              >
                <a
                  href="#contact"
                  className="absolute inset-0 z-10"
                  aria-label={`${project.title} — ${w.viewProject}`}
                >
                  <span className="sr-only">{project.title}</span>
                </a>

                <Image
                  src={project.image}
                  alt={`${project.title} — ${project.result}`}
                  fill
                  className="object-cover object-top transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  sizes={
                    featured
                      ? "(max-width: 1024px) 100vw, 58vw"
                      : "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 42vw"
                  }
                  priority={featured}
                />

                <div className={`work-card-overlay${featured ? "" : " work-card-overlay-side"}`} />
                <div className="work-card-shine" aria-hidden />
                <span className="work-corner work-corner-tl" aria-hidden />
                <span className="work-corner work-corner-br" aria-hidden />
                {featured && (
                  <>
                    <span className="work-corner work-corner-tr" aria-hidden />
                    <span className="work-corner work-corner-bl" aria-hidden />
                  </>
                )}

                <div className={`absolute inset-x-0 bottom-0 z-[1] ${featured ? "p-5 sm:p-7 lg:p-8" : "p-5 sm:p-6"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <span className="work-index">{index}</span>
                    <span className="work-category">{project.category}</span>
                  </div>
                  <h3
                    className={`font-display mt-3 font-bold tracking-tight text-[var(--foreground)] transition-all duration-300 group-hover:text-brand-gradient ${
                      featured ? "text-2xl sm:mt-4 sm:text-3xl" : "text-xl"
                    }`}
                  >
                    {project.title}
                  </h3>
                  <p
                    className={`mt-1.5 leading-relaxed text-[var(--muted-foreground)] ${
                      featured
                        ? "max-w-md text-sm sm:mt-2 sm:text-[0.95rem]"
                        : "text-xs sm:text-sm"
                    }`}
                  >
                    {project.result}
                  </p>
                  <span className={`work-cta ${featured ? "mt-5" : "mt-4"}`}>
                    {w.viewProject} <span aria-hidden>↗</span>
                  </span>
                </div>
              </AnimateIn>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
